/**
 * ExplorerTable.tsx
 * Data grid showing folders, entities, and versions
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import {
  FolderOpen as FolderIcon,
  Download as ImportIcon,
} from '@mui/icons-material';
import type {
  EntityExplorerConfig,
  ExplorerNode,
} from '../../../types/entity-explorer.types';
import { ENTITY_TYPE_LABELS } from '../../../types/entity-explorer.types';
import { styles } from './entityExplorer.styles';

interface ExplorerTableProps {
  items: ExplorerNode[];
  config: EntityExplorerConfig;
  sortField: SortField;
  sortOrder: 'asc' | 'desc';
  useServerSorting: boolean;
  onSortChange: (field: SortField, order: 'asc' | 'desc') => void;
  onRowClick: (node: ExplorerNode) => void;
  onImportClick: (node: ExplorerNode) => void;
}

type SortField = 'name' | 'entityType' | 'createdBy' | 'createdAt';

/**
 * Data grid table
 */
export function ExplorerTable({
  items,
  config,
  sortField,
  sortOrder,
  useServerSorting,
  onSortChange,
  onRowClick,
  onImportClick,
}: ExplorerTableProps) {
  const [localSortBy, setLocalSortBy] = useState<SortField>('name');
  const [localSortOrder, setLocalSortOrder] = useState<'asc' | 'desc'>('asc');
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Helper: Extract user name from createdBy
   * Shows only the name, no design ID
   */
  const getCreatedByName = (createdBy: any): string => {
    if (!createdBy) return '—';
    // If object, extract name
    if (typeof createdBy === 'object') {
      return createdBy.name || createdBy.fullName || createdBy.username || '—';
    }
    // If string (ObjectId), return as-is
    if (typeof createdBy === 'string') return createdBy;
    return '—';
  };

  /**
   * Helper: Map entityType to display label
   */
  const getTypeLabel = (entityType: string): string => {
    return ENTITY_TYPE_LABELS[entityType as keyof typeof ENTITY_TYPE_LABELS]?.toLowerCase() || entityType.toLowerCase();
  };

  /**
   * Get column widths based on mode
   * In select-folder mode, we only show Name column (full width)
   * In import mode, we show Name/Type/CreatedBy/Action (25% each)
   */
  const getColumnWidth = (): string => {
    return config.mode === 'select-folder' ? '100%' : '25%';
  };

  /**
   * Handle scroll - close tooltip when scrolling
   */
  React.useEffect(() => {
    const handleScroll = () => {
      setOpenTooltip(null);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  /**
   * Handle sort click
   */
  const handleSort = (field: SortField) => {
    if (useServerSorting) {
      const nextOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
      onSortChange(field, nextOrder);
      return;
    }

    if (localSortBy === field) {
      setLocalSortOrder(localSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setLocalSortBy(field);
      setLocalSortOrder('asc');
    }
  };

  /**
   * Sort items
   */
  const displaySortBy = useServerSorting ? sortField : localSortBy;
  const displaySortOrder = useServerSorting ? sortOrder : localSortOrder;

  const sortedItems = [...items].sort((a, b) => {
    let aVal: any = a[displaySortBy];
    let bVal: any = b[displaySortBy];

    // Handle createdBy object - extract name for comparison
    if (displaySortBy === 'createdBy') {
      aVal = typeof aVal === 'object' ? (aVal?.name || aVal?.fullName || '') : (aVal || '');
      bVal = typeof bVal === 'object' ? (bVal?.name || bVal?.fullName || '') : (bVal || '');
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    // Handle name and entityType - case-insensitive sorting
    if (displaySortBy === 'name' || displaySortBy === 'entityType') {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }

    if (displaySortBy === 'createdAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (displaySortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  return (
    <TableContainer 
      ref={containerRef}
      sx={{ 
        overflow: 'auto',
        border: '1px solid #dee2e6',
        maxHeight: 'calc(100vh - 100px)',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow sx={{ 
            backgroundColor: '#fff',
            height: '45px',
            '& .MuiTableCell-head': {
              backgroundColor: '#fff',
              fontWeight: 600,
              fontSize: '0.9rem',
              borderRight: '1px solid #dee2e6',
              borderBottom: '2px solid #dee2e6',
              padding: '10px 12px',
              verticalAlign: 'middle',
            },
            '& .MuiTableCell-head:last-child': {
              borderRight: 'none',
            }
          }}>
            {/* Name column */}
            <TableCell sx={{ width: '25%' }}>
              <TableSortLabel
                active={displaySortBy === 'name'}
                direction={displaySortOrder}
                onClick={() => handleSort('name')}
              >
                📄 Name
              </TableSortLabel>
            </TableCell>

            {/* Type column */}
            <TableCell sx={{ width: '25%' }}>
              <TableSortLabel
                active={displaySortBy === 'entityType'}
                direction={displaySortOrder}
                onClick={() => handleSort('entityType')}
              >
                Type
              </TableSortLabel>
            </TableCell>

            {/* Created By column */}
            <TableCell sx={{ width: '25%' }}>
              <TableSortLabel
                active={displaySortBy === 'createdBy'}
                direction={displaySortOrder}
                onClick={() => handleSort('createdBy')}
              >
                Created By
              </TableSortLabel>
            </TableCell>

            {/* Action column */}
            <TableCell align="center" sx={{ width: '25%' }}>
              Action
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedItems.map((item) => {
            const label = getTypeLabel(item.entityType);
            
            return (
              <TableRow
                key={item.id}
                sx={{ 
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  height: '45px',
                  '&:hover': {
                    backgroundColor: '#f9f9f9',
                  },
                  '& .MuiTableCell-body': {
                    borderRight: '1px solid #dee2e6',
                    borderBottom: '1px solid #dee2e6',
                    padding: '10px 12px',
                    verticalAlign: 'middle',
                  },
                  '& .MuiTableCell-body:last-child': {
                    borderRight: 'none',
                  }
                }}
                onClick={() => {
                  onRowClick(item);
                }}
              >
                {/* Name - Folder icon for all items with tooltip for long names */}
                <TableCell sx={{ width: '25%', overflow: 'hidden', p: '8px 12px', wordBreak: 'break-word' }}>
                  <Tooltip 
                    title={item.name} 
                    open={openTooltip === item.id}
                    onOpen={() => setOpenTooltip(item.id)}
                    onClose={() => setOpenTooltip(null)}
                    enterDelay={300}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                      <FolderIcon sx={{ fontSize: '18px', color: '#1976d2', flexShrink: 0 }} />
                      <div style={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap', 
                        fontSize: '0.95rem',
                        maxWidth: '100%'
                      }}>
                        {item.name}
                      </div>
                    </Box>
                  </Tooltip>
                </TableCell>

                {/* Type column */}
                <TableCell sx={{ width: '25%', overflow: 'hidden', wordBreak: 'break-word' }}>
                  <span style={{ 
                    fontSize: '0.85rem',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {label}
                  </span>
                </TableCell>

                {/* Created By column */}
                <TableCell sx={{ width: '25%', overflow: 'hidden', wordBreak: 'break-word' }}>
                  <span style={{ 
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {getCreatedByName(item.createdBy)}
                  </span>
                </TableCell>

                {/* Action column */}
                <TableCell align="center" sx={{ width: '25%' }} onClick={(e) => e.stopPropagation()}>
                  {item.kind === 'version' ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        onImportClick(item);
                      }}
                      sx={{ 
                        p: 0.5, 
                        minWidth: 'auto', 
                        color: '#1976d2', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        whiteSpace: 'nowrap'
                      }}
                      title="Import"
                    >
                      <ImportIcon sx={{ fontSize: '18px', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.85rem', textTransform: 'none' }}>Import</span>
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
